/* istanbul ignore file */

import { setTranslator } from "@42.nl/ui";
import { locale as setLocale } from "dayjs";
import i18next, { default as i18n, exists, t } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import { first } from "lodash";
// Load dayjs locales
import "dayjs/locale/en";
import "dayjs/locale/nl";
import { initReactI18next, useTranslation } from "react-i18next";

/**
 * @todo our customers are also using other languages,
 * but we should probably just replace dayjs with luxon anyway
 **/
function setDayjsLocale(language: string) {
  switch (language) {
    case "nl":
      setLocale("nl");
      break;
    case "en":
    default:
      setLocale("en");
      break;
  }
}

const MISSING_TRANSLATION_AFFIX = "?";
const LABEL_KEY_SEPARATOR = ".";

export const configureI18n = (supportedLanguages: string[]) => {
  const queryParameters = new URLSearchParams(location.search);
  const languageParam = queryParameters.get("language");

  let defaultLanguage;
  if (languageParam && supportedLanguages.includes(languageParam)) {
    defaultLanguage = languageParam;
  } else {
    defaultLanguage = first(supportedLanguages) ?? "EN";
  }

  // Set the locale for Day.js
  setDayjsLocale(defaultLanguage);

  /* istanbul ignore next */
  // eslint-disable-next-line import/no-named-as-default-member
  i18next
    .use(HttpApi)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      detection: {
        lookupQuerystring: "language"
      },
      backend: {
        loadPath: "/api/labels/{{lng}}/{{ns}}"
      },
      supportedLngs: supportedLanguages,
      fallbackLng: defaultLanguage,
      //debug: process.env.NODE_ENV === "development",
      interpolation: {
        escapeValue: false
      },
      keySeparator: LABEL_KEY_SEPARATOR,
      appendNamespaceToMissingKey: true,
      parseMissingKeyHandler: (key) => `${key}${MISSING_TRANSLATION_AFFIX}`,
      ns: ["42-ui", "validation", "sort"],
      react: {
        bindI18n: "languageChanged loaded",
        bindI18nStore: "added removed",
        nsMode: "default",
        useSuspense: true
      }
    });

  /**
   * Translation provider for @42.nl/ui, which maps on the namespace `42-ui`.
   * @see public/locales/en/42-ui.json
   */
  setTranslator(({ key, data }) => t(`42-ui:${key.toUpperCase()}`, data));

  return i18n;
};

export default i18next;

export const isMissingTranslation = (result?: string) => {
  return result?.endsWith(MISSING_TRANSLATION_AFFIX) || result === "";
};

export const translateWithFallback = (key: string, fallback: string) => {
  if (exists(key)) {
    return t(key);
  }
  return fallback;
};

export const useTranslationKeyWithFallback = (
  key?: string,
  fallback?: string
) => {
  const { t } = useTranslation();
  if (!key) {
    return fallback;
  }
  const result = t(key);
  return isMissingTranslation(result) ? fallback : result;
};
